import type { MailboxAction, Problem } from '../api';
import { FieldGroup, RadioGroup, TextArea, TextField } from '../components/form';
import { Alert, Switch } from '../components/ui';
import type { Draft } from './draft';

const MAILBOX_ACTIONS: { value: MailboxAction; label: string; hint: string }[] = [
  { value: 'DISABLE', label: 'Disable the account', hint: 'Mail is kept, nobody can sign in. The usual choice.' },
  {
    value: 'CONVERT_TO_SHARED',
    label: 'Convert to a shared mailbox',
    hint: 'Colleagues keep access to the mail. Frees the licence.',
  },
  { value: 'DELETE', label: 'Delete the mailbox', hint: 'Permanent once retention expires. Rarely right.' },
];

/**
 * What happens to the account and its mail. Every field here is something IT
 * would otherwise have to guess at, and guessing wrong is either a licence
 * bill or a mailbox somebody needed.
 */
export function StepAccounts({
  draft,
  onChange,
  problems,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  problems: Problem[];
}) {
  const set = <K extends keyof Draft['employee']>(key: K, value: Draft['employee'][K]) =>
    onChange({ ...draft, employee: { ...draft.employee, [key]: value } });

  const problemFor = (field: string) => problems.find((problem) => problem.field === field)?.message;
  const e = draft.employee;

  return (
    <div className="space-y-8">
      <FieldGroup title="The mailbox" description="What happens to their mail after they go.">
        <RadioGroup
          label="Mailbox"
          required
          value={e.mailboxAction}
          onChange={(value) => set('mailboxAction', value as MailboxAction | '')}
          error={problemFor('mailboxAction')}
          options={MAILBOX_ACTIONS.map((action) => ({ value: action.value, label: action.label }))}
        />

        {e.mailboxAction && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {MAILBOX_ACTIONS.find((action) => action.value === e.mailboxAction)?.hint}
          </p>
        )}

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <TextField
            label="Keep the mailbox for"
            value={e.mailboxRetentionDays}
            onChange={(value) => set('mailboxRetentionDays', value.replace(/[^0-9]/g, ''))}
            hint="Days. Leave blank to follow whatever the company retention policy says."
            maxLength={4}
          />
        </div>

        {e.mailboxAction === 'DELETE' && (
          <div className="mt-4">
            <Alert tone="warning">
              Deletion cannot be undone once retention expires. Unless there is a specific reason, disabling
              the account keeps the mail available and costs nothing.
            </Alert>
          </div>
        )}
      </FieldGroup>

      <FieldGroup
        title="Out of office"
        description="What anyone writing to them will get back. Written here because you know how it should be worded; IT only sets it."
      >
        <TextArea
          label="Auto-reply message"
          value={e.outOfOfficeMessage}
          onChange={(value) => set('outOfOfficeMessage', value)}
          rows={4}
          placeholder={`${e.fullName || 'This person'} has left New Horizon Copper. Please contact …`}
          maxLength={2000}
        />
      </FieldGroup>

      <FieldGroup title="Forwarding" description="Where their incoming mail should go, and until when.">
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Forward to"
            value={e.forwardToName}
            onChange={(value) => set('forwardToName', value)}
            hint="Name or email of the colleague picking it up."
            maxLength={160}
          />

          <TextField
            label="Forward until"
            type="date"
            value={e.forwardUntil}
            onChange={(value) => set('forwardUntil', value)}
            error={problemFor('forwardUntil')}
            hint="Required once forwarding is set, so it does not run for ever."
          />
        </div>
      </FieldGroup>

      <FieldGroup title="Licences" description="Money, and the step most often forgotten.">
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-800">
          <Switch
            id="release-licences"
            checked={e.releaseLicences}
            onChange={(next) => set('releaseLicences', next)}
            label="Release their licences"
          />
          <div className="min-w-0">
            <label htmlFor="release-licences" className="block cursor-pointer text-sm font-medium text-slate-900 dark:text-white">
              Release their licences
            </label>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
              Free the Microsoft 365 and other paid seats once the mailbox is dealt with. Turn this off only if
              the account has to keep a licence for a stated reason.
            </p>
          </div>
        </div>
      </FieldGroup>
    </div>
  );
}
